import { Form } from 'antd';
import { FC, memo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
    SiteGroupFormFields,
    SiteGroupFormInputs,
    SiteGroupsQueryKeys,
    useGetOneByIdSiteGroupsQuery,
    useUpdateSiteGroupsMutation
} from '@/modules/SiteGroups';

import { SiteGroupsFormView } from './SiteGroupsFormView';
import { siteGroupsFormValidationSchema } from './validation';

interface Props {
  siteGroupId: string;
}

export const SiteGroupsEditFormContainer: FC<Props> = memo(({ siteGroupId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<SiteGroupFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof SiteGroupFormInputs>>(null);

  const {
    data: siteGroupData,
    error: siteGroupError,
    isLoading: isSiteGroupLoading,
    isError: isSiteGroupError
  } = useGetOneByIdSiteGroupsQuery(siteGroupId)({
    enabled: !!siteGroupId
  });

  const { mutate: updateSiteGroupMutation, isLoading: updateSiteGroupIsLoading } = useUpdateSiteGroupsMutation(siteGroupId)({
    onSuccess: async (data) => {
      appMessageApi.success('Групу сайтів успішно змінено!');

      await queryClient.refetchQueries([SiteGroupsQueryKeys.GetAll]);
      await queryClient.refetchQueries([SiteGroupsQueryKeys.GetOneById, siteGroupId]);

      navigate(AppRoutes.SiteGroupsById.replace(':siteGroupId', data.id));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof SiteGroupFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const handleFinish = (values: SiteGroupFormFields): void => {
    setFormErrors(null);
    updateSiteGroupMutation(values);
  };

  const handleBack = (): void => {
    navigate(AppRoutes.SiteGroupsById.replace(':siteGroupId', siteGroupId));
  };

  const handleFinishFailed = (): void => {
    setFormErrors(null);
  };

  return (
    <FallbackedLayout
      isLoading={ isSiteGroupLoading }
      isError={ isSiteGroupError }
      error={ siteGroupError }
      data={ siteGroupData }
    >
      { (siteGroup) => (
        <SiteGroupsFormView
          formName="site-group-edit"
          formType="edit"
          form={ form }
          formErrors={ formErrors }
          initialValues={{
            name: siteGroup.name,
            siteIds: siteGroup.sites.map((site) => site.id)
          }}
          isFormLoading={ updateSiteGroupIsLoading }
          validationSchema={ siteGroupsFormValidationSchema }
          handleBack={ handleBack }
          handleFinish={ handleFinish }
          handleFinishFailed={ handleFinishFailed }
        />
      ) }
    </FallbackedLayout>
  );
});
